import React, { useCallback, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { CostSourceChip } from '../../../components/CostSourceChip';
import type { CostBasis } from '../../contexts/ComponentCardContext';
import type { ReplacementQuantityDisplay } from '../../../../../Helpers/maintenanceCostUtils';
import { QuantityPopover } from './QuantityPopover';

interface ReplacementRowProps {
    lifetimeYears: string;
    yearsRemaining: number | null;
    /** Shown instead of the years when given (Massredigering: "Blandade") */
    yearsRemainingLabel?: string;
    /** The Mängd shown; null without a lifetime */
    quantity: ReplacementQuantityDisplay | null;
    /**
     * Given for a kr/st lifetime outside Massredigering: the Mängd is the replacement's
     * antal, editable here. Absent, the Mängd is read-only (m², lm, or a selection).
     */
    unitCount?: {
        value: number | undefined;
        onChange: (count: number) => void;
        onClear: () => void;
    };
    displayCost: string;
    costExplanation: string;
    costBasis: CostBasis;
    isHighlighted: boolean;
    isOverdue: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onOpenReplacementCostModal: () => void;
    onOpenConditionModal: () => void;
    disabled?: boolean;
}

/**
 * Replacement activity row component
 * Displays and allows editing of component replacement details
 */
export const ReplacementRow: React.FC<ReplacementRowProps> = ({
    lifetimeYears,
    yearsRemaining,
    yearsRemainingLabel,
    quantity,
    unitCount,
    displayCost,
    costExplanation,
    costBasis,
    isHighlighted,
    isOverdue,
    onMouseEnter,
    onMouseLeave,
    onOpenReplacementCostModal,
    onOpenConditionModal,
    disabled = false
}) => {
    const [isUnitCountOpen, setIsUnitCountOpen] = useState(false);
    const unitCountButtonRef = useRef<HTMLButtonElement>(null);
    const closeUnitCount = useCallback(() => setIsUnitCountOpen(false), []);
    const hasOwnUnitCount = unitCount?.value !== undefined;
    // An offert fixes the cost, so the antal no longer drives it and is not edited
    const canEditUnitCount = unitCount !== undefined && !quantity?.muted;

    return (
        <div
            className={`activity-row replacement-row ${isHighlighted ? 'highlighted' : ''} ${isOverdue ? 'overdue' : ''}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="activity-name">
                <span>Ersätt komponent</span>
            </div>

            <div className="activity-interval">
                <span className={disabled ? 'text-muted' : ''}>{lifetimeYears} år</span>
            </div>

            {/* Quantity driving the replacement cost (muted when a kostnad enligt offert overrides it); a kr/st antal is edited here */}
            <div className="activity-area" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span
                        className={disabled || !quantity || quantity.muted ? 'text-muted' : ''}
                        style={hasOwnUnitCount && !quantity?.muted ? {
                            color: disabled ? undefined : '#0d6efd',
                            fontWeight: 500,
                            cursor: 'help'
                        } : undefined}
                        title={quantity?.title ?? (hasOwnUnitCount ? 'Anpassat antal för ersättningen' : undefined)}
                    >
                        {quantity?.text ?? '–'}
                    </span>
                    {canEditUnitCount && (
                        <button
                            ref={unitCountButtonRef}
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={() => setIsUnitCountOpen(open => !open)}
                            disabled={disabled}
                            title="Redigera antal för ersättningen"
                            aria-label="Redigera antal för Ersätt komponent"
                            style={{ lineHeight: 1 }}
                        >
                            <Pencil size={14} className={disabled ? 'text-muted' : 'text-primary'} />
                        </button>
                    )}
                </div>
                {isUnitCountOpen && canEditUnitCount && unitCount && (
                    <QuantityPopover
                        label="Antal (st)"
                        hints={['Används bara för kostnadsberäkningen i underhållsplanen, t.ex. antal lägenheter för en stam. Ändrar inte antalet komponenter i byggnaden.']}
                        initialValue={unitCount.value ?? 1}
                        unitLabel="st"
                        wholeNumbers
                        canReset={hasOwnUnitCount}
                        resetTitle="Återställ till 1 st"
                        onSave={unitCount.onChange}
                        onReset={unitCount.onClear}
                        onClose={closeUnitCount}
                        anchorRef={unitCountButtonRef}
                    />
                )}
            </div>

            <div className="activity-year">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={disabled ? 'text-muted' : ''}>
                        {yearsRemainingLabel ?? (yearsRemaining !== null ? `${Math.max(0, Math.floor(yearsRemaining))} år kvar` : '–')}
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenConditionModal}
                        disabled={disabled}
                        title="Ändra skick"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={disabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>

            <div className="activity-cost">
                <div className="cost-display-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={`cost-value-wrapper ${disabled ? 'text-muted' : ''}`}>
                        <span className="cost-value" style={{ cursor: 'help' }}>
                            <CostSourceChip source={costBasis.source} hasAttachments={costBasis.hasAttachments} muted={disabled}>
                                {displayCost ? `${parseInt(displayCost).toLocaleString('sv-SE')} kr` : '0 kr'}
                            </CostSourceChip>
                        </span>
                        <span className="cost-tooltip">{costExplanation}</span>
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenReplacementCostModal}
                        disabled={disabled}
                        title="Redigera ersättningskostnad"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={disabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>
        </div>
    );
};
